import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { api, money } from "../lib/api";
import Reveal from "../components/Reveal";

export default function SetPage() {
  const { id } = useParams();
  const [retroSet, setRetroSet] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/sets/${id}`)
      .then(async (r) => {
        setRetroSet(r.data);
        const prodRes = await api.get("/products");
        setPieces(prodRes.data.filter((p) => r.data.product_ids.includes(p.id)));
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="set-not-found" className="flex min-h-[60vh] items-center justify-center bg-cream px-4">
        <div className="card-chunky max-w-md p-10 text-center">
          <p className="font-display text-3xl font-extrabold uppercase">Set not found</p>
          <Link to="/" className="btn-chunky mt-6 bg-blue px-6 py-3 text-sm text-white">Back home</Link>
        </div>
      </div>
    );
  }

  if (!retroSet) {
    return <div data-testid="set-loading" className="flex min-h-[60vh] items-center justify-center bg-cream font-mono text-sm">Loading...</div>;
  }

  const total = pieces.reduce((s, p) => s + p.price, 0);

  return (
    <div data-testid="set-page">
      <section className="border-b-2 border-charcoal py-14 lg:py-20" style={{ backgroundColor: retroSet.color }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Link to="/" data-testid="set-back-link" className="inline-flex items-center gap-2 font-mono text-xs font-bold uppercase opacity-70 hover:opacity-100">
            <ArrowLeft className="h-4 w-4" /> All starter packs
          </Link>
          <p className="mt-6 inline-block rounded-full border-2 border-charcoal bg-white px-4 py-1.5 font-mono text-xs font-bold uppercase shadow-hard-sm">
            Starter Pack / {pieces.length} pieces
          </p>
          <h1 data-testid="set-name" className="mt-4 font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            {retroSet.name}
          </h1>
          <p className="mt-3 font-display text-lg font-bold uppercase text-charcoal/70">{retroSet.tagline}</p>
          <p className="mt-4 max-w-xl text-base text-charcoal/85">{retroSet.description}</p>
          <p data-testid="set-total" className="mt-6 font-display text-4xl font-extrabold text-charcoal">
            {money(total)} <span className="font-mono text-xs font-normal uppercase">full kit, bought piece by piece</span>
          </p>
        </div>
      </section>

      <section className="bg-cream py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">What's in the kit</h2>
          <div className="mt-8 flex flex-col gap-5">
            {pieces.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div data-testid={`set-item-${p.id}`} className="card-chunky flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
                  <Link to={`/product/${p.id}`} className="h-40 w-full shrink-0 overflow-hidden rounded-2xl border-2 border-charcoal sm:h-32 sm:w-32" style={{ backgroundColor: p.tile_color }}>
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] font-bold uppercase text-charcoal/60">
                      Piece {i + 1} / {p.brand} / {p.era}
                    </p>
                    <Link to={`/product/${p.id}`} className="font-display text-xl font-extrabold uppercase tracking-tight hover:text-blue">
                      {p.name}
                    </Link>
                    <p className="mt-1 text-sm text-charcoal/70">{p.tagline}</p>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end">
                    <span className="font-display text-2xl font-extrabold">{money(p.price)}</span>
                    <a
                      href={p.affiliate_url}
                      target="_blank"
                      rel="sponsored noopener"
                      data-testid={`set-item-buy-${p.id}`}
                      className="btn-chunky bg-charcoal px-5 py-2.5 text-xs text-cream hover:bg-blue"
                    >
                      Buy on {p.retailer} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="card-chunky flex flex-col items-start justify-between gap-4 bg-white p-6 sm:flex-row sm:items-center">
              <p className="max-w-xl text-sm text-charcoal/75">
                <span className="font-display font-extrabold uppercase">No bundle checkout here.</span> Each piece links to its own
                retailer listing — buy one, buy them all, your call. Affiliate disclosure: we may earn a commission.
              </p>
              <Link to="/shop" data-testid="set-shop-more" className="btn-chunky bg-blue px-6 py-3 text-sm text-white hover:bg-coral">
                Keep browsing <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
